// The checkout SDK version each provider loads, set on the CHECKOUT integration
// (`config.primer.sdkVersion`, `config.yuno.sdkVersion`). Without one — no row, an
// empty value, or anything malformed — each loader keeps the version it ships with,
// so a pipe loads exactly what it did before the setting existed.
// Dependency-free so the builder and the co-located test (bare `node --test`) can use it.
import type { CheckoutProvider } from './checkoutProvider'

/** Registry key and API type of the integration that carries the checkout's settings. */
export const CHECKOUT_INTEGRATION_TYPE = 'CHECKOUT'

/** Stored shape of a CHECKOUT row's config (the API's `CheckoutIntegrationConfigDto`). */
export type CheckoutIntegrationConfig = { [P in CheckoutProvider]?: { sdkVersion?: string } }

// The spellings each CDN serves a build under, minus the `v` of its path. Primer
// publishes exact versions only (`v2.62` is a 403); Yuno also serves a floating minor
// (`1.11` tracks the latest 1.11.x). The API's checkout config DTOs validate the
// same patterns — change them together. Digits and dots only: the value lands in a
// script URL.
const SDK_VERSION_FORMATS: Record<CheckoutProvider, RegExp> = {
  primer: /^\d+\.\d+\.\d+$/,
  yuno: /^\d+\.\d+(\.\d+)?$/,
}

/** The configured version in its CDN spelling (no `v`), or undefined when unset or malformed. */
export const normalizeSdkVersion = (
  provider: CheckoutProvider,
  value: unknown,
): string | undefined => {
  const version = typeof value === 'string' ? value.trim().replace(/^v/i, '') : ''
  return SDK_VERSION_FORMATS[provider].test(version) ? version : undefined
}
